import { useContext, useEffect, useState } from 'react';
import {
  Card,
  Flex,
  Spin,
  Typography,
  Input,
  Checkbox,
  ConfigProvider,
} from 'antd';
import Icon from '@ant-design/icons';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as DmsVideoEmpty } from '../../../assets/icons/dms-video-empty.svg';
import { ReactComponent as DmsVideoEmptyWhite } from '../../../assets/no-data-white.svg';
import { ReactComponent as SortLatest } from '../../../assets/icons/sort-latest.svg';
import { useTheme } from '../../../context/ThemeContext';
import { DARK, HYPHEN } from '../../../utilities/constants';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import CustomRangePicker from '../../../components/CustomRangePicker/CustomRangePicker';
import { ReactComponent as Search } from '../../../assets/icons/search.svg';
import { ReactComponent as SearchBlack } from '../../../assets/icons/search-black.svg';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import TripsListCard from '../../VehicleMonitoring/components/TripsListCard';
import PropTypes from 'prop-types';

const { Text, Title } = Typography;
const PastTripsList = ({ selectedTrip, setSelectedTrip }) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const { theme } = useTheme();
  const [tripsList, setTripsList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState(['', '']);
  const [searchText, setSearchText] = useState('');
  const [isSosChecked, setIsSosChecked] = useState(false);
  const [isSortAsc, setIsSortAsc] = useState(false);

  const fetchPastTripsList = async (pageNum = 1) => {
    try {
      setLoading(true);
      if (pageNum === 1) {
        setTripsList([]);
        setSelectedTrip(null);
      }
      const payload = {
        filter: {
          trip_status: 'Inactive,',
          trip_id_vehicle_no_sticker_id: searchText,
          start_date: dateRange[0],
          end_date: dateRange[1],
          is_sos_alerted: isSosChecked,
        },
        sort: {
          actual_trip_start_time: isSortAsc ? 'asc' : 'desc',
        },
      };
      const { data } = await API.getVodTripReports(pageNum, 12, payload);

      if (data) {
        const formattedData = data?.results?.map((trip, index) => ({
          key: `${pageNum}-${index}`,
          trip_id: trip?.trip_id || HYPHEN,
          driver_id: trip?.driver_id || HYPHEN,
          drivername: trip?.drivername || HYPHEN,
          driver_phone_numbers: trip?.driver_phone_numbers || [],
          registration: trip?.registration || HYPHEN,
          ridebuddy_deviceid: trip?.ridebuddy_deviceid || HYPHEN,
          trip_status: trip?.trip_status || HYPHEN,
          trip_start_time: trip?.trip_start_time || HYPHEN,
          trip_end_time: trip?.trip_end_time || HYPHEN,
          duty_start_time: trip?.duty_start_time || HYPHEN,
          duty_end_time: trip?.duty_end_time || HYPHEN,
          created_timestamp: trip?.created_timestamp || HYPHEN,
          ls_update_timestamp: trip?.ls_update_timestamp || HYPHEN,
          actual_trip_start_time: trip?.actual_trip_start_time || HYPHEN,
          actual_trip_end_time: trip?.actual_trip_end_time || HYPHEN,
          sosCount: trip?.sos_count || 0,
          criticalCount: trip?.critical_count || 0,
          majorCount: trip?.major_anomaly_count || 0,
          minorCount: trip?.minor_anomaly_count || 0,
          sticker_id: trip?.sticker_id,
        }));

        if (pageNum === 1) {
          setTripsList(formattedData);
        } else {
          setTripsList(prev => [...prev, ...formattedData]);
        }

        setPage(data?.next_page_number);
        setHasMore(data?.next_page_number !== null);
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch trips list! Please try again after sometime.'
        )
      );
      setTripsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      (dateRange[0] !== '' && dateRange[1] !== '') ||
      (dateRange[0] === '' && dateRange[1] === '')
    ) {
      fetchPastTripsList(1);
    }
  }, [dateRange]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchPastTripsList(page);
    }
  };

  const handleTripClick = trip => {
    setSelectedTrip(trip);
  };

  const handleSearch = () => {
    fetchPastTripsList(1);
  };

  const onSearchTextChange = e => {
    setSearchText(sanitizeInput(e.target.value));
  };

  const handleSosCheckboxChange = e => {
    setIsSosChecked(e.target.checked);
  };

  useEffect(() => {
    fetchPastTripsList(1);
  }, [isSosChecked, isSortAsc]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorBgContainer: '#71767C',
            colorBorder: '#71767C',
          },
        },
      }}
    >
      <Card
        variant="borderless"
        className="tripslist-card tripslist-card-surveillance"
      >
        <Flex vertical gap={20}>
          <Title level={4} style={{ marginTop: '0px' }}>
            Search trips
          </Title>
          <Flex vertical gap={8}>
            <CustomRangePicker setDateRange={setDateRange} />
            <Input
              onChange={onSearchTextChange}
              onPressEnter={() => handleSearch()}
              size="large"
              placeholder="Trip ID/ Sticker ID"
              suffix={
                <Icon
                  component={theme === DARK ? Search : SearchBlack}
                  width={20}
                  height={20}
                  onClick={() => handleSearch()}
                />
              }
              className="input-search tripslist-search"
              disabled={loading}
            />
          </Flex>
          <Flex justify="space-between">
            <Checkbox checked={isSosChecked} onChange={handleSosCheckboxChange}>
              SOS
            </Checkbox>
            <Flex
              gap={8}
              onClick={() => {
                setIsSortAsc(!isSortAsc);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Icon component={SortLatest} />
              <Text style={{ color: '#007BC0' }}>Latest</Text>
            </Flex>
          </Flex>
          {loading && tripsList?.length === 0 ? (
            <Flex justify="center" align="center" style={{ height: 300 }}>
              <Spin size="large" />
            </Flex>
          ) : tripsList?.length === 0 ? (
            <Flex justify="center" align="center" style={{ marginTop: 40 }}>
              <EmptyCard
                image={
                  <Icon
                    component={
                      theme === DARK ? DmsVideoEmpty : DmsVideoEmptyWhite
                    }
                  />
                }
                description={
                  <Flex
                    vertical
                    className="full-width"
                    gap={32}
                    justify="center"
                    align="center"
                  >
                    <Flex
                      vertical
                      className="full-width"
                      gap={12}
                      justify="center"
                      align="center"
                    >
                      <Text strong style={{ fontSize: '16px' }}>
                        There are no Trips!
                      </Text>
                    </Flex>
                  </Flex>
                }
              />
            </Flex>
          ) : (
            <div
              id="scrollableDiv"
              className="w-full past-trips-list-scroll-div"
            >
              <InfiniteScroll
                dataLength={tripsList.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  <div className="infinite-scroll-loader">
                    <Spin size="large" />
                  </div>
                }
                scrollableTarget="scrollableDiv"
                endMessage={
                  <div className="infinite-scroll-end-message">
                    No more events to load
                  </div>
                }
                style={{ overflow: 'hidden' }}
              >
                <Flex vertical>
                  {tripsList?.map(trip => (
                    <TripsListCard
                      item={trip}
                      key={trip?.trip_id}
                      handleTripClick={handleTripClick}
                      backgroundColor={
                        trip.key === selectedTrip?.key
                          ? 'var(--selected-bg-color, #D0D4D8)'
                          : 'var(--secondary-fill-color, #2E3033)'
                      }
                      color={
                        trip.key === selectedTrip?.key &&
                        'var(--selected-text-color, #000000)'
                      }
                    />
                  ))}
                </Flex>
              </InfiniteScroll>
            </div>
          )}
        </Flex>
      </Card>
    </ConfigProvider>
  );
};

PastTripsList.propTypes = {
  selectedTrip: PropTypes.object,
  setSelectedTrip: PropTypes.func.isRequired,
};

export default PastTripsList;
